import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Eye, EyeOff, XCircle } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { apiClient } from "../../lib/apiClient";
import { ApiError, describeError } from "../../lib/errors";

import { BrandMark } from "./BrandMark";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import styles from "./AuthLayout.module.css";

interface VerifyResponse {
  email?: string;
  role?: string;
  expires_at?: string;
}

type VerifyState =
  | { kind: "loading" }
  | { kind: "ok"; data: VerifyResponse }
  | { kind: "invalid" };

export function ResetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id");
  const token = params.get("token");

  const [verify, setVerify] = useState<VerifyState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A link missing either parameter never reaches the probe; the render below
    // already treats it as invalid, so there is nothing to record here.
    if (!id || !token) return;
    let cancelled = false;
    // GET-verify probe — the backend validates the reset token up front:
    // 200 → render the account-preview card with the real {email, role};
    // 404 invalid_token (or any error) → render the invalid-token state
    // immediately, without waiting for a submit.
    apiClient
      .get<VerifyResponse>(
        `/api/admin/auth/reset/verify?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`,
      )
      .then((r) => {
        if (cancelled) return;
        setVerify({ kind: "ok", data: r.data ?? {} });
      })
      .catch(() => {
        if (cancelled) return;
        setVerify({ kind: "invalid" });
      });
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  if (!id || !token || verify.kind === "invalid") {
    return (
      <div className={`${styles.page}`}>
        <div className={`${styles.card} ${styles.cardWide}`}>
          <BrandMark />
          <div className={styles.hero}>
            <div className={`${styles.heroIcon} ${styles.heroIconDanger}`}>
              <XCircle size={32} />
            </div>
            <h1 className={styles.title}>This link is no longer valid</h1>
            <div className={styles.subtitle}>
              The password reset link has expired or has already been used.
              Reset links are valid for 60 minutes and can only be used once.
            </div>
            <Button
              size="lg"
              className={styles.submit}
              onClick={() => navigate("/forgot")}
            >
              Request a new link
            </Button>
          </div>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.link}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
              }}
              onClick={() => navigate("/login")}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (verify.kind === "loading") {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWide}`}>
          <BrandMark />
          <div className={styles.subtitle}>Checking your link…</div>
        </div>
      </div>
    );
  }

  const mismatch =
    confirm.length > 0 && confirm !== password ? "Passwords don't match." : null;
  const matches = confirm.length > 0 && confirm === password;
  const submitDisabled =
    submitting ||
    password.length < 12 ||
    password !== confirm ||
    confirm.length === 0;

  const previewEmail = verify.data.email;
  const previewRole = verify.data.role;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitDisabled) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await apiClient.post<{ csrf_token?: string }>(
        "/api/admin/auth/reset",
        { id, token, password },
      );
      // Backend returns 200 with a fresh session cookie + CSRF token — the
      // user is auto-logged-in. Adopt the new CSRF token and go straight to
      // the app.
      if (r.data?.csrf_token) {
        apiClient.setCSRF(r.data.csrf_token);
      }
      navigate("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "invalid_token" || err.code === "expired") {
          setVerify({ kind: "invalid" });
          return;
        }
        setError(describeError(err.code));
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${styles.cardWide}`}>
        <BrandMark />
        <h1 className={styles.title}>Choose a new password</h1>
        <div className={styles.subtitle}>
          Your new password will replace the current one and sign you out
          everywhere else.
        </div>

        {error && (
          <div style={{ marginBottom: 14 }}>
            <Alert variant="danger" title="Couldn't reset password">
              {error}
            </Alert>
          </div>
        )}

        {previewEmail ? (
          <div
            className={styles.accountPreview}
            style={{ marginBottom: 18 }}
            data-testid="account-preview"
          >
            <Avatar name={previewEmail.split("@")[0]} size={32} />
            <div className={styles.info}>
              <span className={styles.email}>{previewEmail}</span>
              {previewRole && (
                <Badge variant={previewRole === "super_admin" ? "admin" : "user"}>
                  {previewRole}
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <div
            className={styles.accountPreview}
            style={{ marginBottom: 18 }}
            data-testid="account-preview-placeholder"
          >
            <Avatar name="?" size={32} variant="neutral" />
            <div className={styles.info}>
              <span className={styles.email}>Reset password for your account</span>
            </div>
          </div>
        )}

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div>
            <Input
              type={showPw ? "text" : "password"}
              label="New password"
              hintRight="min 12 characters"
              autoComplete="new-password"
              required
              value={password}
              disabled={submitting}
              onChange={(e) => setPassword(e.target.value)}
              trailingIcon={
                <button
                  type="button"
                  className={styles.toggleVis}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((v) => !v)}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
            <PasswordStrengthMeter password={password} />
          </div>
          <div>
            <Input
              type={showPw2 ? "text" : "password"}
              label="Confirm new password"
              autoComplete="new-password"
              required
              value={confirm}
              disabled={submitting}
              onChange={(e) => setConfirm(e.target.value)}
              trailingIcon={
                <button
                  type="button"
                  className={styles.toggleVis}
                  aria-label={
                    showPw2 ? "Hide confirm password" : "Show confirm password"
                  }
                  onClick={() => setShowPw2((v) => !v)}
                  tabIndex={-1}
                >
                  {showPw2 ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
            {mismatch && (
              <div
                className={`${styles.matchHelper} ${styles.err}`}
                role="status"
              >
                {mismatch}
              </div>
            )}
            {matches && (
              <div
                className={`${styles.matchHelper} ${styles.ok}`}
                role="status"
              >
                <Check size={12} /> Passwords match
              </div>
            )}
          </div>
          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={submitDisabled}
            loading={submitting}
          >
            {submitting ? "Saving…" : "Set new password & sign in"}
          </Button>
        </form>

        <div className={styles.footer}>
          All other sessions will be signed out for security.
        </div>
      </div>
    </div>
  );
}
